// core/stores/feedStore.ts — the Home tabs the server computes, one page at a time.
//
// Following, Discover and Trending each hold the ordered id list the server picked
// (`GET /posts/?feed=<tab>`), the offset of the next page (null on the last one) and,
// for Following, who spread each post into the feed. The posts themselves live in
// postStore, exactly as the For You tab's do (see forYouStore): this store only says
// WHICH posts a tab shows and in what order.
//
// Lives outside the Home page so postsService.createPost can put a freshly published
// post at the top of Following: the default tab has to show a member their own post
// the moment they publish it.
import { create } from 'zustand';
import type { Post } from '@/core/models/post';

export type PagedFeedKey = 'following' | 'discover' | 'trending';
export type SpreadBy = NonNullable<Post['spreadBy']>;

export interface FeedPage {
  ids: string[];
  next: number | null;
  spreadBy: Record<string, SpreadBy>;
}

interface FeedState {
  /** Null until the tab has been loaded this session. */
  feeds: Record<PagedFeedKey, FeedPage | null>;
  /** Replace a tab with its first page. */
  setFirstPage: (key: PagedFeedKey, page: FeedPage) => void;
  /** Add the following page to a tab (ids already shown are skipped). */
  appendPage: (key: PagedFeedKey, page: FeedPage) => void;
  /** Put a just-published post at the top of a loaded tab; a no-op until it has loaded. */
  prependId: (key: PagedFeedKey, id: string) => void;
  /** Back to "nothing loaded" (sign-out, account switch). */
  clear: () => void;
}

const EMPTY: Record<PagedFeedKey, FeedPage | null> = {
  following: null,
  discover: null,
  trending: null,
};

export const useFeedStore = create<FeedState>((set) => ({
  feeds: { ...EMPTY },
  setFirstPage: (key, page) => set((s) => ({ feeds: { ...s.feeds, [key]: page } })),
  appendPage: (key, page) =>
    set((s) => {
      const current = s.feeds[key];
      if (!current) return { feeds: { ...s.feeds, [key]: page } };
      const seen = new Set(current.ids);
      return {
        feeds: {
          ...s.feeds,
          [key]: {
            ids: [...current.ids, ...page.ids.filter((id) => !seen.has(id))],
            next: page.next,
            spreadBy: { ...current.spreadBy, ...page.spreadBy },
          },
        },
      };
    }),
  prependId: (key, id) =>
    set((s) => {
      const current = s.feeds[key];
      if (!current || current.ids.includes(id)) return s;
      return { feeds: { ...s.feeds, [key]: { ...current, ids: [id, ...current.ids] } } };
    }),
  clear: () => set({ feeds: { ...EMPTY } }),
}));
